import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Card, Alert, Spinner, InfoRow, Input, Tabs, Textarea, Select, Toggle, MultiSelect, Tooltip, ScopeBadges, TagsInput } from './shared';
import SkillResourcePanel from './SkillResourcePanel';

const SKILL_TYPES = ['general', 'expert', 'tool', 'routing', 'template'];
const OWNER_TYPES = ['system', 'partner', 'user'];

const NEW_SKILL = {
    id: '', name: '', description: '', content: '',
    skill_type: 'general', owner_type: 'system', owner_id: '',
    is_shareable: false, shared_with: [],
    metadata: {},
    tags: [],
    environment: '',
};

export default function AdminSkillForm() {
    const { skillId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isResourcesTab = location.pathname.endsWith('/resources');
    const isEdit = location.pathname.includes('/edit');
    const isCreate = !skillId;
    const isView = !!skillId && !isEdit;
    const activeTab = isResourcesTab ? 'resources' : 'content';

    const [data, setData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [availableTools, setAvailableTools] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [newMetaKey, setNewMetaKey] = useState('');
    const [newMetaValue, setNewMetaValue] = useState('');

    useEffect(() => { loadData(); }, [skillId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [skillData, toolsRes, envRes] = await Promise.all([
                isCreate ? Promise.resolve(NEW_SKILL) : adminApi.getSkill(skillId),
                adminApi.listTools().catch(() => ({ tools: [] })),
                adminApi.listEnvironments().catch(() => []),
            ]);
            setData(skillData);
            setFormData({ ...skillData });
            setAvailableTools((toolsRes.tools || []).map(t => ({ value: t.name, label: t.name })));
            setEnvironments(Array.isArray(envRes) ? envRes : []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load skill');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleMetaChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            metadata: { ...prev.metadata, [key]: value },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const cleanMeta = Object.fromEntries(
                Object.entries(formData.metadata || {}).filter(([, v]) => {
                    if (v === undefined || v === null || v === '') return false;
                    if (Array.isArray(v) && v.length === 0) return false;
                    return true;
                })
            );
            const payload = { ...formData, metadata: cleanMeta };
            if (isCreate) {
                await adminApi.createSkill(payload);
            } else {
                await adminApi.updateSkill(payload.id, payload);
            }
            navigate('/admin/skills');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!data && !isCreate) return <Alert variant="error">Skill not found</Alert>;

    const title = isCreate ? 'Create Skill' : isView ? data.name : `Edit: ${data.name}`;
    const resourceCount = data?.resource_count || 0;
    const showTabs = !isCreate;
    const meta = (isView ? data?.metadata : formData?.metadata) || {};
    const allowedTools = meta['allowed-tools'] || [];

    const tabs = [
        { id: 'content', label: 'Content' },
        { id: 'resources', label: 'Resources', count: resourceCount },
    ];

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/skills" className="text-gray-400 hover:text-gray-600 shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                    </svg>
                </Link>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
                        {isView && data.is_builtin && <Badge variant="success">Built-in</Badge>}
                        {isView && <Badge variant="outline">{data.skill_type || 'general'}</Badge>}
                    </div>
                    {skillId && (
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm text-gray-500">{skillId}</span>
                            {data && <ScopeBadges environment={data.environment} tags={data.tags} />}
                        </div>
                    )}
                </div>
                {isView && (
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => navigate(`/admin/skills/${skillId}/edit`)}>Edit</Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            {showTabs && (
                <Tabs tabs={tabs} active={activeTab} onChange={(tab) => {
                    const base = isEdit ? `/admin/skills/${skillId}/edit` : `/admin/skills/${skillId}`;
                    navigate(tab === 'resources' ? `${base}/resources` : base, { replace: true });
                }} className="mb-4" />
            )}

            {isView ? (
                activeTab === 'content' ? (
                    <div className="space-y-4">
                        <Card title="General">
                            <InfoRow label="ID" value={data.id} mono />
                            <InfoRow label="Name" value={data.name} />
                            <InfoRow label="Type" value={<Badge variant="outline">{data.skill_type || 'general'}</Badge>} />
                            <InfoRow label="Description" value={data.description || '-'} wrap />
                            <InfoRow label="Owner" value={<Badge variant="info">{data.owner_type || 'system'}</Badge>} />
                            {data.owner_id && <InfoRow label="Owner ID" value={data.owner_id} mono />}
                            <InfoRow label="Environment" value={<Badge variant="outline">{data.environment || 'default'}</Badge>} />
                            <InfoRow
                                label="Tags"
                                value={
                                    (data.tags || []).length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {(data.tags || []).map((t) => (
                                                <Badge key={t} variant="info">#{t}</Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )
                                }
                            />
                            <InfoRow label="Shareable" value={data.is_shareable ? 'Yes' : 'No'} />
                            <InfoRow label="Built-in" value={data.is_builtin ? 'Yes' : 'No'} />
                            {data.file_path && <InfoRow label="File Path" value={data.file_path} mono />}
                            {data.s3_url && (
                                <InfoRow label="S3" value={
                                    <a href={data.s3_url} target="_blank" rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs break-all">
                                        {data.s3_key}
                                    </a>
                                } />
                            )}
                        </Card>
                        {allowedTools.length > 0 && (
                            <Card title="Allowed Tools">
                                <div className="flex flex-wrap gap-1">
                                    {allowedTools.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                                </div>
                            </Card>
                        )}
                        {Object.keys(meta).filter(k => k !== 'allowed-tools').length > 0 && (
                            <Card title="Metadata">
                                {meta.license && <InfoRow label="License" value={meta.license} />}
                                {meta.compatibility && <InfoRow label="Compatibility" value={meta.compatibility} />}
                                {Object.entries(meta)
                                    .filter(([k]) => !['allowed-tools', 'license', 'compatibility'].includes(k))
                                    .map(([key, value]) => (
                                        <InfoRow key={key} label={key} value={typeof value === 'string' ? value : JSON.stringify(value)} mono />
                                    ))
                                }
                            </Card>
                        )}
                        {data.content && (
                            <Card title="Content (SKILL.md)">
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[70vh]">
                                    {data.content}
                                </pre>
                            </Card>
                        )}
                    </div>
                ) : (
                    <SkillResourcePanel skillId={skillId} readOnly />
                )
            ) : (
                activeTab === 'content' || isCreate ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <Card title="General">
                            <Input
                                label="ID" hint="lowercase, hyphens only"
                                value={formData.id}
                                onChange={(e) => handleChange('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                                placeholder="my-custom-skill" pattern="^[a-z0-9-]+$" required disabled={isEdit}
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <Input label={<span className="flex items-center gap-1">Name <Tooltip text="Hiển thị trong skill listing reminder cho LLM" /></span>} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="My Custom Skill" required />
                                <Select label={<span className="flex items-center gap-1">Type <Tooltip text="Phân loại skill: general, expert, tool, routing, template" /></span>} options={SKILL_TYPES} value={formData.skill_type || 'general'} onChange={(e) => handleChange('skill_type', e.target.value)} />
                            </div>
                            <Textarea label={<span className="flex items-center gap-1">Description <Tooltip text="Mô tả ngắn giúp LLM quyết định khi nào cần đọc skill này" /></span>} rows={3} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="A brief description of what this skill does" />
                            <div className="grid grid-cols-2 gap-3">
                                <Select label={<span className="flex items-center gap-1">Owner Type <Tooltip text="Ai sở hữu skill: system (mặc định), partner, user" /></span>} options={OWNER_TYPES} value={formData.owner_type || 'system'} onChange={(e) => handleChange('owner_type', e.target.value)} />
                                <Input label="Owner ID" value={formData.owner_id || ''} onChange={(e) => handleChange('owner_id', e.target.value)} placeholder="Optional" />
                            </div>
                            <Select
                                label={<span className="flex items-center gap-1">Environment <Tooltip text="Content variant axis. Cùng id khác env = A/B variants. Bỏ trống = default env." /></span>}
                                value={formData.environment || ''}
                                options={[{ value: '', label: '— default env —' }, ...environments.map(e => ({ value: e.id, label: `${e.id} ${e.is_default ? '(default)' : ''}` }))]}
                                onChange={(e) => handleChange('environment', e.target.value)}
                            />
                            <TagsInput
                                label="Tags"
                                helpText="Agents can opt-in by tag via config.skills.tags = [...] — replaces explicit include/exclude lists."
                                value={formData.tags || []}
                                onChange={(tags) => handleChange('tags', tags)}
                            />
                            <Toggle label="Shareable" checked={formData.is_shareable === true} onChange={(val) => handleChange('is_shareable', val)} />
                        </Card>

                        <Card title={<span className="flex items-center gap-1">Allowed Tools <Tooltip text="Tools mà skill được phép sử dụng. Persist vào frontmatter 'allowed-tools'. Hiện chưa enforce runtime, dùng làm doc cho LLM." /></span>}>
                            <MultiSelect
                                options={availableTools}
                                selected={allowedTools}
                                onChange={(val) => handleMetaChange('allowed-tools', val.length > 0 ? val : [])}
                                placeholder="No restriction (empty = all)..."
                                loading={!availableTools.length}
                            />
                        </Card>

                        <Card title={<span className="flex items-center gap-1">Metadata <Tooltip text="Persist vào JSONB config.metadata. Upload/sync GitHub tự parse từ YAML frontmatter." /></span>}>
                            <div className="grid grid-cols-2 gap-3">
                                <Input label="License" value={meta.license || ''} onChange={(e) => handleMetaChange('license', e.target.value || undefined)} placeholder="e.g. MIT, Apache-2.0" />
                                <Input label="Compatibility" value={meta.compatibility || ''} onChange={(e) => handleMetaChange('compatibility', e.target.value || undefined)} placeholder="e.g. v2, all" />
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <span className="text-xs font-medium text-gray-500 mb-2 block">Custom fields</span>
                                {Object.entries(meta)
                                    .filter(([k]) => !['allowed-tools', 'license', 'compatibility'].includes(k))
                                    .map(([key, value]) => (
                                        <div key={key} className="flex items-center gap-2 mb-2">
                                            <span className="text-xs font-mono text-gray-500 w-32 shrink-0 truncate">{key}</span>
                                            <input
                                                type="text"
                                                className="flex-1 px-2 py-1 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                                value={typeof value === 'string' ? value : JSON.stringify(value)}
                                                onChange={(e) => handleMetaChange(key, e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const next = { ...formData.metadata };
                                                    delete next[key];
                                                    setFormData(prev => ({ ...prev, metadata: next }));
                                                }}
                                                className="text-gray-400 hover:text-red-500 shrink-0"
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))
                                }
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        className="w-32 shrink-0 px-2 py-1 text-sm border rounded-md font-mono dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                        value={newMetaKey}
                                        onChange={(e) => setNewMetaKey(e.target.value.replace(/\s/g, '-'))}
                                        placeholder="key"
                                    />
                                    <input
                                        type="text"
                                        className="flex-1 px-2 py-1 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                        value={newMetaValue}
                                        onChange={(e) => setNewMetaValue(e.target.value)}
                                        placeholder="value"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const k = newMetaKey.trim();
                                            if (k && !meta[k] && !['allowed-tools', 'license', 'compatibility'].includes(k)) {
                                                handleMetaChange(k, newMetaValue);
                                                setNewMetaKey('');
                                                setNewMetaValue('');
                                            }
                                        }}
                                        disabled={!newMetaKey.trim()}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-300 disabled:cursor-not-allowed shrink-0"
                                    >
                                        + Add
                                    </button>
                                </div>
                            </div>
                        </Card>

                        <Card title={<span className="flex items-center gap-1">Content <Tooltip text="LLM đọc qua read_file('/skills/{id}/SKILL.md') khi skill listing reminder gợi ý." /></span>}>
                            <Textarea label={<span className="flex items-center gap-1">SKILL.md <Tooltip text="YAML frontmatter + Markdown body. Upload/sync tự parse frontmatter vào các field ở trên." /></span>} monospace rows={16} value={formData.content} onChange={(e) => handleChange('content', e.target.value)} placeholder="Enter the skill content in markdown format..." required={isCreate} />
                            {formData.content && (
                                <div className="flex justify-end mt-1 text-xs text-gray-400">
                                    <span>{formData.content.length.toLocaleString()} chars</span>
                                </div>
                            )}
                        </Card>

                        <div className="flex items-center justify-end gap-2 pt-2 pb-4">
                            <Button variant="ghost" type="button" onClick={() => navigate('/admin/skills')}>Cancel</Button>
                            <Button type="submit" loading={saving}>{isCreate ? 'Create Skill' : 'Save Changes'}</Button>
                        </div>
                    </form>
                ) : (
                    <SkillResourcePanel skillId={skillId} />
                )
            )}

        </div>
    );
}
